import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const reviews = [
  {
    name: "Nancy Miliou",
    rating: 5,
    date: "πριν από 3 χρόνια",
    dateEn: "3 years ago",
    initial: "N",
    color: "bg-teal-700",
    text: "Άμεση τεχνική εξυπηρέτηση σε 24ωρη ημερήσια βάση. Με αυτή την εταιρεία ο ανελκυστήρας μας πλέον δεν χαλάει. Ευγενικός κ πρόθυμος ο συντηρητής.",
    textEn: "Immediate technical service on a 24-hour daily basis. With this company, our elevator no longer breaks down. Polite and helpful maintenance staff.",
  },
  {
    name: "Demetria COD",
    rating: 5,
    date: "πριν από 2 χρόνια",
    dateEn: "2 years ago",
    initial: "D",
    color: "bg-blue-600",
  },
  {
    name: "Spirosvas",
    rating: 5,
    date: "πριν από 2 χρόνια",
    dateEn: "2 years ago",
    initial: "S",
    color: "bg-teal-600",
  },
  {
    name: "Nikos Koutsokostas",
    rating: 5,
    date: "πριν από 3 χρόνια",
    dateEn: "3 years ago",
    initial: "N",
    color: "bg-green-600",
  },
  {
    name: "Maria Chatzi",
    rating: 5,
    date: "πριν από 3 χρόνια",
    dateEn: "3 years ago",
    initial: "M",
    color: "bg-red-500",
  },
  {
    name: "ΗΛΙΑΣ ΠΑΠΠΑΣ - IP solutions",
    rating: 5,
    date: "πριν από 3 χρόνια",
    dateEn: "3 years ago",
    initial: "Η",
    color: "bg-purple-600",
  },
  {
    name: "stavros mekeres",
    rating: 5,
    date: "πριν από 3 χρόνια",
    dateEn: "3 years ago",
    initial: "S",
    color: "bg-orange-500",
  },
  {
    name: "Basilis Sel",
    rating: 5,
    date: "πριν από 4 χρόνια",
    dateEn: "4 years ago",
    initial: "B",
    color: "bg-pink-600",
  },
  {
    name: "Sivoukidis sales",
    rating: 5,
    date: "πριν από 5 χρόνια",
    dateEn: "5 years ago",
    initial: "S",
    color: "bg-gray-600",
  },
];

const GoogleReviews = () => {
  const { t, language } = useLanguage();
  const averageRating = 5.0;
  const totalReviews = 9;

  return (
    <section className="py-20 bg-background">
      <div className="container">
        {/* Header with Google branding */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <svg viewBox="0 0 24 24" className="w-8 h-8" aria-label="Google">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-2xl font-semibold text-foreground">{t("reviews.title")}</span>
          </div>
          
          {/* Rating summary */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <span className="text-5xl font-bold text-foreground">{averageRating}</span>
            <div className="text-left">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className="w-6 h-6 fill-yellow-400 text-yellow-400" 
                  />
                ))}
              </div>
              <span className="text-muted-foreground text-sm">
                {totalReviews} {t("reviews.count")}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {reviews.map((review, index) => (
            <motion.div
              key={review.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="bg-card border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Author */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full ${review.color} flex items-center justify-center`}>
                  <span className="text-white font-semibold text-sm">
                    {review.initial}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground text-sm truncate">{review.name}</div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>{t("reviews.reviewFrom")}</span>
                    <svg viewBox="0 0 24 24" className="w-3 h-3 inline">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Google</span>
                  </div>
                </div>
              </div>
              
              {/* Review text if exists */}
              {"text" in review && review.text && (
                <p className="text-foreground/80 text-xs mb-3 line-clamp-3">
                  {language === "en" && review.textEn ? review.textEn : review.text}
                </p>
              )}
              
              {/* Stars and date */}
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" 
                    />
                  ))}
                </div>
                <span className="text-muted-foreground text-xs">
                  · {language === "en" && review.dateEn ? review.dateEn : review.date}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Link to Google */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <a
            href="https://www.google.com/maps/place/LIFTCO"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
          >
            {t("reviews.seeAll")}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default GoogleReviews;
